import { defineStore } from 'pinia';
import { useUserStore } from './userStore';
import { INTEGRATION_TYPES } from '../constants/integrationTypes';
import IntegrationService from '@/services/integrationService';
import axios from 'axios';

export const useIntegrationStore = defineStore('integration', {
  state: () => ({
    integrations: [],
    formIntegrationMappings: {}, // { formId: { integrationId: { enabled: true, settings: {} } } }
    // Cache of fully-hydrated integrations (incl. `settings` JSONB) keyed by
    // id. Populated on demand by ensureIntegrationDetails — the list endpoint
    // returns slim records without `settings`.
    integrationDetailsCache: {},
    loading: false,
    error: null,
    integrationTypes: INTEGRATION_TYPES,
  }),

  getters: {
    // Get integration settings for a specific form
    getIntegrationSettings: (state) => (formId, integrationId) => {
      return state.formIntegrationMappings[formId]?.[integrationId]?.settings || {};
    },

    // Check if integration is enabled for a form
    isIntegrationEnabled: (state) => (formId, integrationId) => {
      return !!state.formIntegrationMappings[formId]?.[integrationId]?.enabled;
    },

    // Get all integrations with their form-specific status and settings
    getFormIntegrations: (state) => (formId) => {
      return state.integrations.map((integration) => ({
        ...integration,
        enabled: !!state.formIntegrationMappings[formId]?.[integration.id]?.enabled,
        formSettings: state.formIntegrationMappings[formId]?.[integration.id]?.settings || {},
      }));
    },

    // Get only enabled integrations for a form
    getEnabledIntegrations: (state) => (formId) => {
      return state.integrations.filter(
        (integration) => state.formIntegrationMappings[formId]?.[integration.id]?.enabled
      );
    },

    // Get default form settings for an integration type
    getDefaultFormSettings: () => (integrationType) => {
      const integrationConfig = INTEGRATION_TYPES[integrationType];
      if (!integrationConfig?.formSettingsSchema) return {};

      const defaultSettings = {};
      Object.entries(integrationConfig.formSettingsSchema).forEach(([key, schema]) => {
        if (schema.default !== undefined) {
          defaultSettings[key] = schema.default;
        }
      });
      return defaultSettings;
    },

    // Get form settings schema for an integration type
    getFormSettingsSchema: () => (integrationType) => {
      return INTEGRATION_TYPES[integrationType]?.formSettingsSchema || {};
    },

    // Get integration settings with defaults merged in
    getIntegrationSettingsWithDefaults: (state, getters) => (formId, integrationId) => {
      const integration = state.integrations.find((i) => i.id === integrationId);
      if (!integration) return {};

      // Use the INTEGRATION_TYPES directly instead of calling getter
      const integrationConfig = INTEGRATION_TYPES[integration.type];
      if (!integrationConfig?.formSettingsSchema) return {};

      const defaultSettings = {};
      Object.entries(integrationConfig.formSettingsSchema).forEach(([key, schema]) => {
        if (schema.default !== undefined) {
          defaultSettings[key] = schema.default;
        }
      });

      const currentSettings =
        state.formIntegrationMappings[formId]?.[integrationId]?.settings || {};

      return { ...defaultSettings, ...currentSettings };
    },

    // Get all available integration types
    getAvailableIntegrationTypes: () => {
      return Object.entries(INTEGRATION_TYPES).map(([key, config]) => ({
        type: key,
        ...config,
      }));
    },

    // Check if an integration type supports form-specific settings
    hasFormSettings: () => (integrationType) => {
      return !!(
        INTEGRATION_TYPES[integrationType]?.formSettingsSchema &&
        Object.keys(INTEGRATION_TYPES[integrationType].formSettingsSchema).length > 0
      );
    },
  },

  actions: {
    async fetchIntegrations() {
      // De-duplicate concurrent calls. Multiple callers (background prefetch
      // in userStore, page onMounted hooks) can fire fetchIntegrations within
      // the same tick before the first response lands. Returning the same
      // in-flight promise prevents the duplicate /integrations request.
      if (this._fetchIntegrationsInflight) {
        return this._fetchIntegrationsInflight;
      }

      const promise = (async () => {
        try {
          this.loading = true;
          const response = await axios.get('/integrations');
          this.integrations = response.data;

          // Update formIntegrationMappings from the included formConnections
          this.formIntegrationMappings = {};
          this.integrations.forEach((integration) => {
            integration.form_integrations.forEach((connection) => {
              const formId = connection.form_id;
              if (!this.formIntegrationMappings[formId]) {
                this.formIntegrationMappings[formId] = {};
              }
              this.formIntegrationMappings[formId][integration.id] = {
                enabled: connection.enabled,
                settings: connection.settings || {},
              };
            });
          });

          return response.data;
        } catch (error) {
          this.error = error.message;
          throw error;
        } finally {
          this.loading = false;
          this._fetchIntegrationsInflight = null;
        }
      })();

      this._fetchIntegrationsInflight = promise;
      return promise;
    },

    async fetchFormIntegrations(integrationId) {
      try {
        const response = await axios.get(`/integrations/${integrationId}/forms`);
        // Update the formIntegrationMappings state
        const mappings = response.data.reduce((acc, mapping) => {
          if (!acc[mapping.formId]) acc[mapping.formId] = {};
          acc[mapping.formId][mapping.integrationId] = {
            enabled: mapping.enabled,
            settings: mapping.settings || {},
          };
          return acc;
        }, {});
        this.formIntegrationMappings = mappings;
        return response.data;
      } catch (error) {
        console.error('Failed to fetch form integrations:', error);
        throw error;
      }
    },

    /**
     * Returns the fully-hydrated integration (incl. `settings` JSONB and full
     * `form_integrations` rows) for the given id, fetching once and caching.
     * The list endpoint returns summary fields only, so consumers that need
     * `settings` (the edit modal) must go through this action.
     *
     * @param {string} id
     * @param {{ force?: boolean }} [options]
     */
    async ensureIntegrationDetails(id, { force = false } = {}) {
      if (!id) return null;

      if (!force && this.integrationDetailsCache[id]) {
        return this.integrationDetailsCache[id];
      }

      try {
        const integration = await IntegrationService.getById(id);
        if (integration) {
          this.integrationDetailsCache = {
            ...this.integrationDetailsCache,
            [id]: integration,
          };
        }
        return integration;
      } catch (error) {
        console.error('Failed to fetch integration details:', error);
        throw error;
      }
    },

    invalidateIntegrationDetails(id) {
      if (!id) return;
      const next = { ...this.integrationDetailsCache };
      delete next[id];
      this.integrationDetailsCache = next;
    },

    async createIntegration(integrationData, formConnections) {
      try {
        this.loading = true;
        const integrationType = INTEGRATION_TYPES[integrationData.type];
        const userStore = useUserStore();

        console.log(integrationData);
        const payload = {
          ...integrationData,
          formConnections: Object.entries(formConnections || {})
            .filter(([_, enabled]) => enabled)
            .reduce((acc, [formId]) => {
              acc[formId] = { enabled: true };
              return acc;
            }, {}),
        };

        console.log(payload);
        const response = await axios.post('/integrations', payload);

        const newIntegration = response.data;
        this.integrations.push(newIntegration);

        // Then create form connections if any
        Object.entries(formConnections).forEach(([formId, enabled]) => {
          if (!this.formIntegrationMappings[formId]) {
            this.formIntegrationMappings[formId] = {};
          }
          if (enabled) {
            this.formIntegrationMappings[formId][newIntegration.id] = {
              enabled: true,
              settings: {},
            };
          }
        });
        console.log(response);
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateIntegration(integrationId, updateData) {
      try {
        this.loading = true;
        const response = await axios.put(`/integrations/${integrationId}`, updateData);
        const index = this.integrations.findIndex((i) => i.id === integrationId);
        if (index !== -1) {
          this.integrations[index] = response.data;
        }
        // Bust the details cache so reopening the edit modal re-fetches with
        // the freshly saved settings rather than showing stale values.
        this.invalidateIntegrationDetails(integrationId);

        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteIntegration(integrationId) {
      try {
        const response = await axios.delete(`/integrations/${integrationId}`);
        if (response.status === 204) {
          this.integrations = this.integrations.filter((i) => i.id !== integrationId);
          this.invalidateIntegrationDetails(integrationId);
        }
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    // Add/remove automatic form triggers
    async updateAutoTriggerForms(integrationId, { formId, enabled }) {
      try {
        const integration = this.integrations.find((i) => i.id === integrationId);
        if (!integration) throw new Error('Integration not found');

        if (enabled) {
          // Add form to auto-trigger list
          integration.autoTriggerForms = [...new Set([...integration.autoTriggerForms, formId])];
        } else {
          // Remove form from auto-trigger list
          integration.autoTriggerForms = integration.autoTriggerForms.filter((id) => id !== formId);
        }

        await axios.put(`/integrations/${integrationId}`, integration);
        return integration;
      } catch (error) {
        this.error = error.message;
        throw error;
      }
    },

    async toggleIntegration(formId, integrationId, enabled) {
      try {
        const response = await axios.post(`/integrations/${integrationId}/forms/${formId}/toggle`, {
          enabled: enabled,
        });

        // Update local state
        if (!this.formIntegrationMappings[formId]) {
          this.formIntegrationMappings[formId] = {};
        }

        if (enabled) {
          // Enable integration
          const integration = this.integrations.find((i) => i.id === integrationId);
          const defaultSettings = integration ? this.getDefaultFormSettings(integration.type) : {};

          this.formIntegrationMappings[formId][integrationId] = {
            enabled: true,
            settings: defaultSettings,
          };

          // Also update the integration's formConnections
          if (integration) {
            if (!integration.form_integrations) integration.form_integrations = [];
            integration.form_integrations.push({
              id: crypto.randomUUID(),
              enabled: true,
              form_id: formId,
              settings: defaultSettings,
            });
          }

          // Initialize form settings with defaults if they exist
          if (Object.keys(defaultSettings).length > 0) {
            try {
              await this.updateIntegrationFormSettings(formId, integrationId, defaultSettings);
            } catch (error) {
              console.warn('Failed to initialize default form settings:', error);
            }
          }
        } else {
          // Disable integration
          delete this.formIntegrationMappings[formId][integrationId];

          // Also update the integration's formConnections
          const integration = this.integrations.find((i) => i.id === integrationId);
          if (integration?.form_integrations) {
            integration.form_integrations = integration.form_integrations.filter(
              (fi) => fi.form_id !== formId
            );
          }
        }
        return response.data;
      } catch (error) {
        console.error('Failed to toggle integration:', error);
        throw error;
      }
    },

    async updateIntegrationFormSettings(formId, integrationId, settings) {
      try {
        const response = await axios.put(
          `/integrations/${integrationId}/forms/${formId}/settings`,
          {
            settings,
          }
        );

        // Update local state
        if (!this.formIntegrationMappings[formId]) {
          this.formIntegrationMappings[formId] = {};
        }
        if (!this.formIntegrationMappings[formId][integrationId]) {
          this.formIntegrationMappings[formId][integrationId] = { enabled: true };
        }
        this.formIntegrationMappings[formId][integrationId].settings = settings;

        return response.data;
      } catch (error) {
        console.error('Failed to update integration form settings:', error);
        throw error;
      }
    },

    // Initialize form settings with defaults when enabling an integration
    async initializeFormSettings(formId, integrationId) {
      const integration = this.integrations.find((i) => i.id === integrationId);
      if (!integration) throw new Error('Integration not found');

      const defaultSettings = this.getDefaultFormSettings(integration.type);

      if (Object.keys(defaultSettings).length > 0) {
        return this.updateIntegrationFormSettings(formId, integrationId, defaultSettings);
      }

      return Promise.resolve();
    },

    // Get form integration settings from API
    async fetchFormIntegrationSettings(formId, integrationId) {
      try {
        const response = await axios.get(`/integrations/${integrationId}/forms/${formId}/settings`);

        // Update local state
        if (!this.formIntegrationMappings[formId]) {
          this.formIntegrationMappings[formId] = {};
        }
        this.formIntegrationMappings[formId][integrationId] = {
          enabled: response.data.enabled,
          settings: response.data.settings || {},
        };

        return response.data;
      } catch (error) {
        if (error.response?.status === 404) {
          // Integration not configured for this form yet
          return null;
        }
        console.error('Failed to fetch form integration settings:', error);
        throw error;
      }
    },

    // Validate form settings against schema
    validateFormSettings(integrationType, settings) {
      const schema = INTEGRATION_TYPES[integrationType]?.formSettingsSchema;
      if (!schema) return { valid: true, errors: [] };

      const errors = [];

      Object.entries(schema).forEach(([key, fieldSchema]) => {
        if (fieldSchema.required && (settings[key] === undefined || settings[key] === '')) {
          errors.push(`${fieldSchema.label || key} is required`);
        }

        // Type validation
        if (settings[key] !== undefined) {
          const value = settings[key];
          switch (fieldSchema.type) {
            case 'string':
              if (typeof value !== 'string') {
                errors.push(`${fieldSchema.label || key} must be a string`);
              }
              break;
            case 'number':
              if (typeof value !== 'number' || isNaN(value)) {
                errors.push(`${fieldSchema.label || key} must be a number`);
              }
              break;
            case 'boolean':
              if (typeof value !== 'boolean') {
                errors.push(`${fieldSchema.label || key} must be a boolean`);
              }
              break;
            case 'object':
              if (typeof value !== 'object' || Array.isArray(value)) {
                errors.push(`${fieldSchema.label || key} must be an object`);
              }
              break;
            case 'array':
              if (!Array.isArray(value)) {
                errors.push(`${fieldSchema.label || key} must be an array`);
              }
              break;
          }
        }
      });

      return {
        valid: errors.length === 0,
        errors,
      };
    },
  },
});
